import React, { useState } from 'react';
import './InfoModal.css';
import placeholderImage from "../../assets/admin/images/placeholder.svg";
import PrimaryButton from "./PrimaryButton";
import plusIconWhite from "../../assets/admin/icons/plus-white.svg";
import PrimaryButtonDark from './PrimaryButtonDark';

const S3_BUCKET_NAME = "resto-online";
const S3_BUCKET_REGION = "eu-north-1";
const S3_BASE_URL = `https://${S3_BUCKET_NAME}.s3.${S3_BUCKET_REGION}.amazonaws.com/`;

const InfoModal = ({ isOpen, onClose, product, onAddToCart }) => {
    const [selectedOptions, setSelectedOptions] = useState({});
    const [error, setError] = useState("");

    if (!isOpen) return null;

    // Prijs berekening
    const originalPrice = product.price?.$numberDecimal
        ? parseFloat(product.price.$numberDecimal)
        : parseFloat(product.price);

    const discountedPrice = product.discount
        ? (originalPrice * (1 - product.discount / 100))
        : null;

    const stockQuantity = product.stockQuantity;
    const isMenu = product.products && Array.isArray(product.products);

    const handleOptionChange = (optionIndex, value) => {
        // Als de waarde een lege string is, stel dan undefined in
        const parsedValue = value === '' ? undefined : parseInt(value);
        setSelectedOptions(prev => ({
            ...prev,
            [optionIndex]: parsedValue
        }));
    };

    const handleMultiSelectChange = (optionIndex, choiceIndex, checked) => {
        const currentSelections = selectedOptions[optionIndex] || [];
        const newSelections = checked
            ? [...currentSelections, choiceIndex]
            : currentSelections.filter(i => i !== choiceIndex);

        // Check if we're within the maxSelections limit
        const option = product.productOptions[optionIndex];
        if (newSelections.length > option.maxSelections) {
            setError(`Je kunt maximaal ${option.maxSelections} keuzes maken voor ${option.name}`);
            return;
        }

        setSelectedOptions(prev => ({
            ...prev,
            [optionIndex]: newSelections
        }));
        setError("");
    };

    const handleAddToCart = () => {
        // Validate required options
        const missingRequired = product.productOptions?.some((option, index) => {
            if (!option.required) return false;
            
            if (option.type === 'text') {
                return !selectedOptions[index]?.trim();
            }
            
            if (option.type === 'select') {
                return selectedOptions[index] === undefined;
            }
            
            if (option.type === 'multiselect') {
                return !selectedOptions[index]?.length;
            }
            
            return false;
        });

        if (missingRequired) {
            setError("Vul alle verplichte opties in");
            return;
        }

        // Calculate total price including options
        let totalPrice = originalPrice;
        if (product.productOptions) {
            product.productOptions.forEach((option, index) => {
                if (option.type === 'select') {
                    const choiceIndex = selectedOptions[index];
                    if (choiceIndex !== undefined) {
                        const choice = option.options[choiceIndex];
                        if (choice && choice.price) {
                            const price = choice.price?.$numberDecimal 
                                ? parseFloat(choice.price.$numberDecimal)
                                : parseFloat(choice.price);
                            totalPrice += price;
                        }
                    }
                } else if (option.type === 'multiselect') {
                    const selections = selectedOptions[index] || [];
                    selections.forEach(choiceIndex => {
                        if (choiceIndex !== undefined) {
                            const choice = option.options[choiceIndex];
                            if (choice && choice.price) {
                                const price = choice.price?.$numberDecimal 
                                    ? parseFloat(choice.price.$numberDecimal)
                                    : parseFloat(choice.price);
                                totalPrice += price;
                            }
                        }
                    });
                }
            });
        }

        // Add to cart with selected options and calculated price
        onAddToCart({
            ...product,
            selectedOptions,
            totalPrice: parseFloat(totalPrice.toFixed(2))
        });
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <button className="modal-close" onClick={onClose}>×</button>
                <div className="modal-header">
                    <h2>{product.name}</h2>
                </div>
                <div className="modal-body">
                    <div className="product-image">
                        <img 
                            src={product.hasImage ? `${S3_BASE_URL}${product._id}?t=${Date.now()}` : placeholderImage} 
                            alt={product.name}
                        />
                    </div>
                    <div className="product-info">
                        <p className="description">{product.description}</p>
                        <div className="price-info">
                            {discountedPrice ? (
                                <>
                                    <span className="original-price">€{originalPrice.toFixed(2)}</span>
                                    <span className="discounted-price">€{discountedPrice.toFixed(2)}</span>
                                </>
                            ) : (
                                <span className="price">€{originalPrice.toFixed(2)}</span>
                            )}
                        </div>
                        {product.collection && (
                            <p className="collection">Categorie: {product.collection}</p>
                        )}
                        {!isMenu && (
                            <p className="stock-info">
                                Voorraad: {stockQuantity ? stockQuantity : "Onbeperkt"}
                            </p>
                        )}
                        {isMenu && product.products.length > 0 && (
                            <div className="menu-products">
                                <h3>Dit menu bevat:</h3>
                                <ul className="products-list">
                                    {product.products.map((menuProduct) => (
                                        <li key={menuProduct._id} className="menu-product-item">
                                            <div className="menu-product-image">
                                                <img 
                                                    src={menuProduct.hasImage ? `${S3_BASE_URL}${menuProduct._id}?t=${Date.now()}` : placeholderImage}
                                                    alt={menuProduct.name}
                                                />
                                            </div>
                                            <div className="menu-product-details">
                                                <h4>{menuProduct.name}</h4>
                                                <p>{menuProduct.description}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {product.productOptions && product.productOptions.length > 0 && (
                            <div className="product-options">
                                <h3>Beschikbare Opties</h3>
                                {product.productOptions.map((option, index) => (
                                    <div key={index} className="option-group">
                                        <label>
                                            {option.name}
                                            {option.required && <span className="required">*</span>}
                                        </label>
                                        {option.type === 'multiselect' && (
                                            <div className="multiselect-options">
                                                {option.options.map((choice, choiceIndex) => {
                                                    const price = choice?.price?.$numberDecimal 
                                                        ? parseFloat(choice.price.$numberDecimal)
                                                        : parseFloat(choice?.price || 0);
                                                    return (
                                                        <label key={choiceIndex} className="checkbox-label">
                                                            <input
                                                                type="checkbox"
                                                                checked={Array.isArray(selectedOptions[index]) && selectedOptions[index].includes(choiceIndex) || false}
                                                                onChange={(e) => handleMultiSelectChange(index, choiceIndex, e.target.checked)}
                                                            />
                                                            <span>
                                                                {choice.name} 
                                                                {price > 0 && <span className="choice-price"> +€{price.toFixed(2)}</span>}
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                                <p className="max-selections">
                                                    Maximaal {option.maxSelections} keuzes
                                                </p>
                                            </div>
                                        )}
                                        {option.type === 'select' && (
                                            <select
                                                value={selectedOptions[index] === undefined ? '' : selectedOptions[index]}
                                                onChange={(e) => handleOptionChange(index, e.target.value)}
                                            >
                                                <option value="">Selecteer een optie</option>
                                                {option.options.map((choice, choiceIndex) => {
                                                    const price = choice?.price?.$numberDecimal 
                                                        ? parseFloat(choice.price.$numberDecimal)
                                                        : parseFloat(choice?.price || 0);
                                                    return (
                                                        <option key={choiceIndex} value={choiceIndex}>
                                                            {choice.name}
                                                            {price > 0 && ` (+€${price.toFixed(2)})`}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        )}
                                        {option.type === 'text' && (
                                            <input
                                                type="text"
                                                value={selectedOptions[index] || ''}
                                                onChange={(e) => handleOptionChange(index, e.target.value)}
                                                placeholder="Voer je tekst in"
                                            />
                                        )}
                                    </div>
                                ))}
                                {error && <p className="error-message">{error}</p>}
                            </div>
                        )}
                        <div className="modal-actions">
                            <PrimaryButtonDark onClick={handleAddToCart}>
                                <div className="g-15-1fr f-s">
                                    <img src={plusIconWhite} alt="add" />
                                    Toevoegen aan Winkelmandje
                                </div>
                            </PrimaryButtonDark>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InfoModal; 